import { MonthPicker } from '@/shared/components'
import {
  Alert,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  SelectListSingle,
  ReportingPeriodBasis
} from '@/common'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

export default function ReportingPeriodBasisCard({
  id,
  state,
  onChange
}: {
  id: string
  state: {
    basisForReportingPeriod: string | ReportingPeriodBasis
    startMonth: number | null
    monthInterval: number | null
  }
  onChange: (payload: {
    basisForReportingPeriod: string | ReportingPeriodBasis
    startMonth: number | null
    monthInterval: number | null
  }) => void
}) {
  const { t } = useTranslation()

  const { basisForReportingPeriod, startMonth, monthInterval } = state
  const [endMonth, setEndMonth] = useState<number | null>(
    startMonth && monthInterval
      ? startMonth + monthInterval - 1 > 12
        ? (startMonth + monthInterval - 1) % 12 || 12
        : startMonth + monthInterval - 1
      : null
  )

  const onBasisForReportingPeriodChange = useCallback(
    (value: string) => {
      setEndMonth(null)

      if (value === 'CALENDAR_YEAR') {
        return onChange({
          ...state,
          basisForReportingPeriod: value as ReportingPeriodBasis,
          startMonth: 1,
          monthInterval: 12
        })
      }

      onChange({
        ...state,
        basisForReportingPeriod: value as ReportingPeriodBasis,
        startMonth: null,
        monthInterval: null
      })
    },
    [state, onChange]
  )

  const onFiscalYearChange = useCallback(
    (value: string) => {
      onChange({
        ...state,
        startMonth: parseInt(value),
        monthInterval: 12
      })
    },
    [state, onChange]
  )

  const onStartMonthChange = useCallback(
    (value: string) => {
      const startMonth = parseInt(value)
      if (endMonth) {
        const monthInterval = (endMonth - startMonth + 12) % 12 || 12
        const monthIntervalWithIncludedStartMonth =
          monthInterval + 1 === 13 ? 12 : monthInterval + 1

        return onChange({
          ...state,
          startMonth,
          monthInterval: monthIntervalWithIncludedStartMonth
        })
      }

      onChange({
        ...state,
        startMonth,
        monthInterval: null
      })
    },
    [endMonth, state, onChange]
  )

  const onEndMonthChange = useCallback(
    (value: string) => {
      const endMonth = parseInt(value)
      setEndMonth(endMonth)

      if (startMonth === 0 || startMonth) {
        const monthInterval = (endMonth - startMonth + 12) % 12 || 12
        const monthIntervalWithIncludedEndMonth = monthInterval + 1 === 13 ? 12 : monthInterval + 1

        return onChange({
          ...state,
          monthInterval: monthIntervalWithIncludedEndMonth
        })
      }

      onChange({
        ...state,
        monthInterval: null
      })
    },
    [startMonth, state, onChange]
  )

  const isMonthintervalLessThan3Months = useMemo(() => {
    return monthInterval && monthInterval < 3
  }, [monthInterval])

  return (
    <div id={id} className="grid gap-4">
      <Card>
        <CardHeader>
          <CardTitle
            title={t('installations:create.sections.reportingPeriodBasis.title')}
          ></CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <Alert type="warning">{t('installations:fields.basisForReportingPeriod.warning')}</Alert>
          <Alert type="info">{t('installations:fields.basisForReportingPeriod.info')}</Alert>
          <SelectListSingle<string>
            className="space-y-2"
            name="basisForReportingPeriod"
            items={[
              {
                label: t(
                  `installations:fields.basisForReportingPeriod.options.CALENDAR_YEAR.label`
                ),
                value: 'CALENDAR_YEAR',
                id: 'CALENDAR_YEAR',
                tooltip: t(
                  'installations:fields.basisForReportingPeriod.options.CALENDAR_YEAR.tooltip'
                ),
                isChecked: basisForReportingPeriod === 'CALENDAR_YEAR'
              },
              {
                label: t(`installations:fields.basisForReportingPeriod.options.FISCAL_YEAR.label`),
                value: 'FISCAL_YEAR',
                id: 'FISCAL_YEAR',
                isChecked: basisForReportingPeriod === 'FISCAL_YEAR',
                isOpen: basisForReportingPeriod === 'FISCAL_YEAR',
                tooltip: t(
                  'installations:fields.basisForReportingPeriod.options.FISCAL_YEAR.tooltip'
                ),
                children: (
                  <div className="w-1/2">
                    <MonthPicker
                      value={startMonth}
                      onChange={onFiscalYearChange}
                      label={t('installations:fields.basisForReportingPeriod.startMonth.label')}
                    />
                  </div>
                )
              },
              {
                label: t(
                  `installations:fields.basisForReportingPeriod.options.DEFINED_BY_ELIGIBLE_MONITORING_AND_VERIFICATION_SCHEME.label`
                ),
                value: 'DEFINED_BY_ELIGIBLE_MONITORING_AND_VERIFICATION_SCHEME',
                id: 'DEFINED_BY_ELIGIBLE_MONITORING_AND_VERIFICATION_SCHEME',
                isChecked:
                  basisForReportingPeriod ===
                  'DEFINED_BY_ELIGIBLE_MONITORING_AND_VERIFICATION_SCHEME',
                tooltip: t(
                  'installations:fields.basisForReportingPeriod.options.DEFINED_BY_ELIGIBLE_MONITORING_AND_VERIFICATION_SCHEME.tooltip'
                ),
                children: (
                  <div className="space-y-2">
                    {isMonthintervalLessThan3Months && (
                      <Alert type="error">
                        {t('installations:fields.basisForReportingPeriod.alert')}
                      </Alert>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                      <MonthPicker
                        value={startMonth}
                        onChange={onStartMonthChange}
                        label={t('installations:fields.basisForReportingPeriod.startMonth.label')}
                      />
                      <MonthPicker
                        value={endMonth}
                        onChange={onEndMonthChange}
                        label={t('installations:fields.basisForReportingPeriod.endMonth.label')}
                      />
                    </div>
                  </div>
                ),
                isOpen:
                  basisForReportingPeriod ===
                  'DEFINED_BY_ELIGIBLE_MONITORING_AND_VERIFICATION_SCHEME'
              }
            ]}
            onChange={onBasisForReportingPeriodChange}
          />
        </CardBody>
      </Card>
    </div>
  )
}
